import { useTranslate } from '@athena/ui'
import type { ReactNode } from 'react'

const RESOURCE_GRID =
  'grid min-w-0 grid-cols-1 gap-3 md:grid-cols-[minmax(0,1.5fr)_minmax(0,1.75fr)_minmax(7rem,0.75fr)_minmax(7rem,0.75fr)] md:items-center md:gap-4'

export interface SubscriptionResourceListProps {
  children: ReactNode
}

/**
 * The shared visual owner for plan exceptions.
 *
 * The list owns the separators, while every record follows the same four
 * columns at useful widths. On small screens the record returns to document
 * order and labels its values explicitly, so long Spanish reasons can wrap
 * without squeezing the status or action into an unreadable row.
 */
export function SubscriptionResourceList({ children }: SubscriptionResourceListProps) {
  const t = useTranslate()

  return (
    <div className="min-w-0">
      <div
        aria-hidden="true"
        className={`${RESOURCE_GRID} hidden border-b border-border pb-2 md:grid`}
      >
        <span className="text-label font-semibold uppercase text-text-muted">
          {t('subscription.resourceName')}
        </span>
        <span className="text-label font-semibold uppercase text-text-muted">
          {t('subscription.resourceReason')}
        </span>
        <span className="text-label font-semibold uppercase text-text-muted">
          {t('subscription.resourceState')}
        </span>
        <span className="text-label font-semibold uppercase text-text-muted md:text-right">
          {t('subscription.resourceAction')}
        </span>
      </div>
      <ul className="divide-y divide-border">{children}</ul>
    </div>
  )
}

export interface SubscriptionResourceRowProps {
  name: ReactNode
  reason: ReactNode
  state: ReactNode
  action?: ReactNode
}

export function SubscriptionResourceRow({
  name,
  reason,
  state,
  action,
}: SubscriptionResourceRowProps) {
  const t = useTranslate()

  return (
    <li className={`${RESOURCE_GRID} py-4 first:pt-0 md:first:pt-4 last:pb-0`}>
      <ResourceValue label={t('subscription.resourceName')} className="font-medium text-text">
        {name}
      </ResourceValue>
      <ResourceValue label={t('subscription.resourceReason')} className="text-text-secondary">
        {reason}
      </ResourceValue>
      <ResourceValue label={t('subscription.resourceState')}>{state}</ResourceValue>
      {action ? (
        <ResourceValue label={t('subscription.resourceAction')} className="md:justify-self-end">
          {action}
        </ResourceValue>
      ) : (
        <span
          aria-hidden="true"
          className="hidden text-body-sm text-text-muted md:block md:text-right"
        >
          —
        </span>
      )}
    </li>
  )
}

function ResourceValue({
  label,
  children,
  className = '',
}: {
  label: string
  children: ReactNode
  className?: string
}) {
  return (
    <div className={`flex min-w-0 flex-col gap-1 text-body-sm ${className}`}>
      <span className="text-label font-semibold uppercase text-text-muted md:sr-only">{label}</span>
      <div className="min-w-0 wrap-anywhere">{children}</div>
    </div>
  )
}
